import { useCallback, useEffect, useState } from "react";

import { IndividualLight } from "@/Common/Api/Individuals/Dto/IndividualLight";
import { HyperLinkRedirectControl } from "@/Common/Components/Buttons/HyperLinkRedirectControl";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { MyleTableHelper } from "@/Common/Helpers/MyleTableHelper";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { Guid } from "@/Common/Models/Guid";
import { PrescriptionRenewalsOrderType, RedirectTo } from "@/Common/Models/UI/Enums";
import { TableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { PrescriptionRenewalRequest } from "../Api/Dto/PrescriptionRenewalRequest";
import { fetchPrescriptionsRenewals, savePrescriptionRenewalFilterSetting } from "../Api/PrescriptionRenewalsClient";
import { PrescriptionRenewalStatus } from "../Common/PrescriptionRenewalStatus";
import { PrescriptionRenewal } from "../Models/PrescriptionRenewal";
import { PrescriptionRenewalFilterSettingModel } from "../Models/PrescriptionRenewalFilterSettingModel";
import { PrescriptionRenewalInboxSettings } from "../Models/PrescriptionRenewalInboxSettings";
import { PrescriptionRenewalModel } from "../Models/PrescriptionRenewalModel";
import { PrescriptionRenewalsFilterSettings } from "./PrescriptionRenewalsFilterSettings";

interface IPrescriptionRenewalsInboxProps {
    onRenewalsClick: (prescriptionRenewalsId: Guid, prescriptionRenewalInboxSettings: PrescriptionRenewalInboxSettings) => void;
    queryCurrentPageNumber: number;
}

function PrescriptionRenewalsInbox(props: IPrescriptionRenewalsInboxProps): JSX.Element {
    const { getText } = useTranslation();
    const saveOptionKey: string = "RenewalsInbox";
    const tableFieldNames = {
        Patient: "Patient",
        Physician: "Physician",
        ReceivedDate: "ReceivedDate",
        AttachedOn: "AttachedOn",
        Source: "Source",
        Status: "Status"
    };

    const [renewalsInboxData, setRenewalsInboxData] = useState([]);
    const [filterVisibility, setfilterVisibility] = useState<boolean>(false);
    const [prescriptionRenewalFilterSetting, setPrescriptionRenewalFilterSetting] = useState<PrescriptionRenewalFilterSettingModel>(PrescriptionRenewalFilterSettingModel.fromCacheModel(cacheStore.UserSettings.PrescriptionRenewalFilterSettings) ?? new PrescriptionRenewalFilterSettingModel());
    const [isColumnOrderingPopupVisible, setIsColumnOrderingPopupVisible] = useState<boolean>(false);
    const [prescriptionRenewalModel, setPrescriptionRenewalModel] = useState<PrescriptionRenewalModel>(new PrescriptionRenewalModel());
    const [prescriptionRenewalInboxSettings, setPrescriptionRenewalInboxSettings] = useState(prescriptionRenewalsStore?.prescriptionRenewalInboxSettings ?? new PrescriptionRenewalInboxSettings());

    const getMyleTableColumnsDefaultOptions = (): MyleTableColumnOptions[] => {
        const prescriptionRenewalsDefaultColumns = [
            getCommonTableColumnOptions(getText("AttachAPatientLabel"), tableFieldNames.Patient, PrescriptionRenewalsOrderType.Patient, 20),
            getCommonTableColumnOptions(getText("PhysicianLabel"), tableFieldNames.Physician, null, 20, false),
            getCommonTableColumnOptions(getText("DateReceivedLabel"), tableFieldNames.ReceivedDate, PrescriptionRenewalsOrderType.ReceivedDate, 15),
            getCommonTableColumnOptions(getText("AttachedOnLabel"), tableFieldNames.AttachedOn, PrescriptionRenewalsOrderType.AttachedOn, 15),
            getCommonTableColumnOptions(getText("SourceLabel"), tableFieldNames.Source, PrescriptionRenewalsOrderType.Source, 15),
            getCommonTableColumnOptions(getText("StatusLabel"), tableFieldNames.Status, PrescriptionRenewalsOrderType.Status, 10)
        ];
        return prescriptionRenewalsDefaultColumns;
    };

    const getCommonTableColumnOptions = (headerText: string, fieldName: string, sortColumn: PrescriptionRenewalsOrderType, WidthPercentage: number, isSortable: boolean = true): MyleTableColumnOptions => new MyleTableColumnOptions({
        HeaderText: headerText,
        FieldName: fieldName,
        RowClickEnabled: true,
        IsSortable: isSortable,
        SortColumn: sortColumn,
        SortDirection: prescriptionRenewalInboxSettings.queryOrderDirection,
        IsSortActive: prescriptionRenewalInboxSettings.queryOrderType === sortColumn,
        WidthPercentageDefault: WidthPercentage,
        sortingMethod: () => tableSort(sortColumn)
    });

    const loadTableRenderOptions = (columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions => {
        const defaultTablePluginOptions = {
            MinColWidth: 50,
            MinTableWidth: 950,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: true,
            Autoresize: true,
            OnColumnWidthChange: null
        };
        return Immutable.fromJS(defaultTablePluginOptions);
    };

    function getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<TableDataSourceItem<PrescriptionRenewal>>(
            columnsOptions,
            column => ({
                renderCell: item => {
                    const rowItem = formatCellText(column.FieldName, item);
                    const rowCssClass = item.Model.isUrgent ? "red-row" : "";
                    return <div className={rowCssClass}>{rowItem}</div>;
                }
            }),
            false
        );
    }

    const formatCellText = (fieldName: string, item: TableDataSourceItem<PrescriptionRenewal>): string | JSX.Element => {
        switch (fieldName) {
            case tableFieldNames.Patient:
                return (
                    <HyperLinkRedirectControl
                        className="text-uppercase"
                        target={RedirectTo.Patient}
                        redirectValue={item.Model.patientContactId}
                        text={NameHelper.formatFullName(item.Model.patientFirstName, item.Model.patientLastName, item.Model.patientPreferredName)}
                    />
                );
            case tableFieldNames.Physician:
                return getFormatedProviders(item.Model.providers);
            case tableFieldNames.ReceivedDate:
                return formatDate(item.Model.documentDate);
            case tableFieldNames.AttachedOn:
                return formatDate(item.Model.attachOnDate);
            case tableFieldNames.Source:
                return item.Model.source;
            case tableFieldNames.Status:
                return getStatus(item.Model.status);
            default:
                return item.Model[fieldName];
        }
    };

    const formatDate = (date: Date): string => LuxonDateTimeParser
        .parse(date)
        ?.toLocal()
        ?.toFormat(`yyyy/LL/dd '${getText("AtLabel")}' HH:mm:ss`) ?? "";

    const getFormatedProviders = (providers: IndividualLight[]): JSX.Element => (
        <>
            {providers?.map(
                provider => (
                    <div key={`prov_${provider.Id}`}>
                        {`${provider.Prefix ? cacheStore.GetLocalizedValueByLookupId(provider.Prefix) : ""} ${provider.LastName} ${provider.FirstName}`}
                    </div>
                )
            )}
        </>
    );

    const getStatus = (status: PrescriptionRenewalStatus): string => {
        switch (status) {
            case PrescriptionRenewalStatus.New:
                return getText("NewLabel");
            case PrescriptionRenewalStatus.Pending:
                return getText("PendingLabel");
            case PrescriptionRenewalStatus.Completed:
                return getText("CompletedLabel");
            default:
                return "";
        }
    };

    const updatePrescriptionRenewalData = useCallback(async (prescriptionRenewalRequestData: PrescriptionRenewalRequest): Promise<void> => {
        const responseData = await fetchPrescriptionsRenewals(prescriptionRenewalRequestData);
        const prescriptionRenewalData = PrescriptionRenewalModel.fromResponseModel(responseData);
        const pageCount: number = Math.round((prescriptionRenewalData.prescriptionRenewalsCount / prescriptionRenewalInboxSettings.pageSize) + 1);

        setPrescriptionRenewalModel(prevState => ({ ...prevState, ...prescriptionRenewalData, totalPageCount: pageCount }));
        setRenewalsInboxData(prescriptionRenewalData.prescriptionRenewals.map(item => new TableDataSourceItem<PrescriptionRenewal>(item)));
    }, [prescriptionRenewalInboxSettings.pageSize]);

    const columnsOptions = cacheStore.Helper.loadTableColumnOptionsFromSettings(saveOptionKey, getMyleTableColumnsDefaultOptions());
    const tableRenderOptions = loadTableRenderOptions(columnsOptions);
    const columnOptionsForOrderColumnsPopup = new MyleHelper().castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

    const tableRefresh = (filterSetting: PrescriptionRenewalFilterSettingModel): void => {
        updatePrescriptionRenewalTable(filterSetting);
    };

    const tableSort = (newOrderType: PrescriptionRenewalsOrderType): void => {
        const { queryOrderType: currentOrderType, queryOrderDirection: currentOrderDirection } = prescriptionRenewalInboxSettings;
        const sortDirection = MyleTableHelper.getNewSortDir(newOrderType, currentOrderType, currentOrderDirection);

        if (prescriptionRenewalInboxSettings.queryOrderType !== newOrderType) {
            setPrescriptionRenewalInboxSettings(p => ({ ...p, queryOrderType: newOrderType }));
        }

        if (prescriptionRenewalInboxSettings.queryOrderDirection !== sortDirection) {
            setPrescriptionRenewalInboxSettings(p => ({ ...p, queryOrderDirection: sortDirection }));
        }
    };

    const updatePrescriptionRenewalTable = useCallback(async (filterSetting: PrescriptionRenewalFilterSettingModel): Promise<void> => {
        const prescriptionRenewalRequestData: PrescriptionRenewalRequest = {
            ResponsibleProviderContactId: prescriptionRenewalInboxSettings.responsibleProviderContactId,
            QueryOrderType: prescriptionRenewalInboxSettings.queryOrderType,
            QueryOrderDirection: prescriptionRenewalInboxSettings.queryOrderDirection,
            SearchText: prescriptionRenewalInboxSettings.searchText,
            QueryCurrentPageNumber: prescriptionRenewalInboxSettings.currentPageNumber,
            PrescriptionRenewalFilterSettings: PrescriptionRenewalFilterSettingModel.toCacheModel(filterSetting)
        };
        await updatePrescriptionRenewalData(prescriptionRenewalRequestData);
    }, [prescriptionRenewalInboxSettings, updatePrescriptionRenewalData]);

    const toggleFilterSetting = (): void => {
        setfilterVisibility(!filterVisibility);
    };

    const settingSaveChanges = (setting: PrescriptionRenewalFilterSettingModel, isSaveSettingsChecked: boolean): void => {
        if (isSaveSettingsChecked) {
            const savedSetting: PrescriptionRenewalFilterSettingModel = { ...setting, dateFrom: null, dateTo: null };
            savePrescriptionRenewalFilterSetting(savedSetting);
        }

        cacheStore.UserSettings.PrescriptionRenewalFilterSettings = PrescriptionRenewalFilterSettingModel.toCacheModel(setting);
        setPrescriptionRenewalFilterSetting(setting);
        setPrescriptionRenewalInboxSettings(p => ({ ...p, currentPageNumber: 1 }));
        toggleFilterSetting();
    };

    useEffect(() => {
        sessionStore.dispatch(
            LocalOperations.PrescriptionRenewal.SetPrescriptionRenewalModel,
            { prescriptionRenewalModel: prescriptionRenewalModel }
        );
    }, [prescriptionRenewalModel]);

    useEffect(() => {
        if (prescriptionRenewalInboxSettings) {
            sessionStore.dispatch(
                LocalOperations.PrescriptionRenewal.SetPrescriptionRenewalInboxSettings,
                { prescriptionRenewalInboxSettings: prescriptionRenewalInboxSettings }
            );
        }
    }, [prescriptionRenewalInboxSettings]);

    useEffect(() => {
        updatePrescriptionRenewalTable(prescriptionRenewalFilterSetting);
    }, [updatePrescriptionRenewalTable, prescriptionRenewalFilterSetting]);

    useEffect(() => {
        sessionStore.dispatchAsync(LocalOperations.PrescriptionRenewal.ClearPrescriptionRenewalCache);
        $(window).trigger("resize");
    }, []);

    return (
        <div className="workspace">
            <div>
                <CommonTableHeaderComponent
                    title={getText("PrescriptionRenewalLabel")}
                    searchText={prescriptionRenewalsStore?.prescriptionRenewalInboxSettings?.searchText ?? ""}
                    searchPlaceholder={getText("SearchLabel")}
                    onSearch={inputText => setPrescriptionRenewalInboxSettings(prevState => ({ ...prevState, searchText: inputText }))}
                    onRefreshClick={() => tableRefresh(prescriptionRenewalFilterSetting)}
                    onSettingsClick={() => toggleFilterSetting()}
                />
                {filterVisibility && (
                    <PrescriptionRenewalsFilterSettings
                        settings={prescriptionRenewalFilterSetting}
                        onSaveChanges={(settings, isSaveSettingsChecked) => settingSaveChanges(settings, isSaveSettingsChecked)}
                        onClose={() => toggleFilterSetting()}
                        onColumnOrderingPopupOpenClick={() => {
                            setIsColumnOrderingPopupVisible(!isColumnOrderingPopupVisible);
                            toggleFilterSetting();
                        }}
                    />
                )}
            </div>
            {isColumnOrderingPopupVisible && (
                <MyleTableColumnOrderControlv2
                    TableColumns={columnOptionsForOrderColumnsPopup}
                    SaveOptionskeyTarget={saveOptionKey}
                    ContainerId="PrescriptionRenewalColumnSettingsPopup"
                    closeColumnOrderPopup={() => setIsColumnOrderingPopupVisible(!isColumnOrderingPopupVisible)}
                />
            )}
            <div id="posref" className="ws-scrollable">
                <div className="myle-doc-tb main-content myle-form">
                    <MyleTable
                        TableRenderOptions={tableRenderOptions}
                        TableId="prescriptionrenewal_inbox_table"
                        Columns={getTableColumns(columnsOptions)}
                        DataSource={renewalsInboxData?.toImmutable()}
                        SaveOptionskeyTarget={saveOptionKey}
                        RowClickFunction={(dataSourceItem: TableDataSourceItem<PrescriptionRenewal>) => props.onRenewalsClick(dataSourceItem.Model.id, prescriptionRenewalInboxSettings)}
                        TableDataCy="prescription-renewals-inbox-table"
                    />
                </div>
            </div>
            <div className="ws-footer">
                <div className="col-lg-6 col-md-6 col-sm-6">
                    <SimplePagerControl
                        PageSize={prescriptionRenewalFilterSetting.queryPageSize}
                        TotalRows={prescriptionRenewalModel.prescriptionRenewalsCount}
                        CurrentPage={prescriptionRenewalInboxSettings.currentPageNumber}
                        changePageFunction={currentPageNumber => setPrescriptionRenewalInboxSettings(prevState => ({ ...prevState, currentPageNumber: currentPageNumber }))}
                    />
                </div>
            </div>
        </div>
    );
}

export { PrescriptionRenewalsInbox };
